<template>
  <nav
    v-if="hasOtherPages"
    class="link-pagination"
    role="navigation"
    aria-label="Навигация по страницам"
  >
    <ul class="link-pagination__list d-flex justify-center align-center mb-0 pa-0">
      <li>
        <a
          v-if="hasPrevious && previousPageNumber"
          :href="getHref(previousPageNumber)"
          class="d-inline-flex align-center justify-center rounded-circle text-decoration-none text-body-1 text-secondary--text bg-gray-0 elevation-1 ml-1 mr-3"
          aria-label="Предыдущая страница"
          @click="onPageClick($event, previousPageNumber)"
        >
          <v-icon
            aria-hidden="true"
            class="pa-2"
            size="16"
          >
            ui-icon-arrow-left
          </v-icon>
        </a>
        <span
          v-else
          class="d-inline-flex align-center justify-center rounded-circle text-body-1 disabled--text bg-gray-0 elevation-1 ml-1 mr-3"
          aria-hidden="true"
        >
          <v-icon
            aria-hidden="true"
            class="pa-2"
            size="16"
          >
            ui-icon-arrow-left
          </v-icon>
        </span>
      </li>

      <li
        v-for="(pageNumber, index) in pageRange"
        :key="`${pageNumber}-${index}`"
      >
        <span
          v-if="pageNumber === currentPage"
          class="link-pagination__item d-inline-flex align-center justify-center rounded-circle text-body-1 primary bg-gray-0--text elevation-2 mx-1"
          aria-current="true"
          :aria-label="`Текущая страница, Страница ${pageNumber}`"
        >
          {{ pageNumber }}
        </span>
        <span
          v-else-if="!pageNumber"
          class="link-pagination__item d-inline-flex align-end justify-center text-body-1 text-secondary--text mx-1"
        >
          ...
        </span>
        <a
          v-else
          :href="getHref(pageNumber)"
          class="link-pagination__item d-inline-flex align-center justify-center rounded-circle text-decoration-none text-body-1 text-secondary--text bg-gray-0 elevation-1 mx-1"
          :aria-label="`Страница ${pageNumber}`"
          @click="onPageClick($event, pageNumber)"
        >
          {{ pageNumber }}
        </a>
      </li>

      <li>
        <a
          v-if="hasNext && nextPageNumber"
          :href="getHref(nextPageNumber)"
          class="d-inline-flex align-center justify-center rounded-circle text-decoration-none text-body-1 text-secondary--text bg-gray-0 elevation-1 ml-3 mr-1"
          aria-label="Следующая страница"
          @click="onPageClick($event, nextPageNumber)"
        >
          <v-icon
            aria-hidden="true"
            class="pa-2"
            size="16"
          >
            ui-icon-arrow-right
          </v-icon>
        </a>
        <span
          v-else
          class="d-inline-flex align-center justify-center rounded-circle text-body-1 disabled--text bg-gray-0 elevation-1 ml-3 mr-1"
          aria-hidden="true"
        >
          <v-icon
            aria-hidden="true"
            class="pa-2"
            size="16"
          >
            ui-icon-arrow-right
          </v-icon>
        </span>
      </li>
    </ul>
  </nav>
</template>

<script setup lang="ts">
import { computed } from 'vue'

type Props = {
  currentPage: number
  pageRange: Array<number | null>
  hasPrevious?: boolean
  hasNext?: boolean
  previousPageNumber?: number | null
  nextPageNumber?: number | null
  getHref: (_page: number) => string
  fetchOnSelect?: boolean
}

type Emits = {
  (_event: 'select', _page: number): void
}

const props = withDefaults(defineProps<Props>(), {
  hasPrevious: false,
  hasNext: false,
  previousPageNumber: null,
  nextPageNumber: null,
  fetchOnSelect: false,
})

const emit = defineEmits<Emits>()

const hasOtherPages = computed(() => props.pageRange.filter(Boolean).length > 1)

function onPageClick(event: MouseEvent, page: number) {
  if (!props.fetchOnSelect) {
    return
  }

  if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey || event.button !== 0) {
    return
  }

  event.preventDefault()
  emit('select', page)
}
</script>

<style lang="scss" scoped>
.link-pagination {
  &__list {
    list-style-type: none;
  }

  &__item {
    width: 32px;
    height: 32px;
  }
}
</style>
